// app/shared/components/markdown/MarkdownDisplayComponent.tsx
"use client";

import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import { FC, useEffect, useState } from "react";
import rehypePrettyCode from "rehype-pretty-code";
import rehypeRaw from "rehype-raw";

import { CodeBlockComponent } from "./CodeBlockComponent";

interface MarkdownDisplayProps {
  markdown: string;
}

const rehypePrettyCodeOptions = {
  theme: "dark-plus",
  keepBackground: true,
};

const components = {
  h1: (props: any) => (
    <h1
      className="text-3xl font-bold my-4 font-heading text-gray-900"
      {...props}
    />
  ),
  h2: (props: any) => (
    <h2
      className="text-2xl font-bold my-3 font-heading text-gray-900"
      {...props}
    />
  ),
  h3: (props: any) => (
    <h3
      className="text-xl font-semibold my-2 font-heading text-gray-900"
      {...props}
    />
  ),
  h4: (props: any) => (
    <h4
      className="text-lg font-semibold my-2 font-heading text-gray-900"
      {...props}
    />
  ),
  h5: (props: any) => (
    <h5
      className="text-base font-semibold my-2 font-heading text-gray-900"
      {...props}
    />
  ),
  h6: (props: any) => (
    <h6
      className="text-sm font-semibold my-2 font-heading text-gray-900"
      {...props}
    />
  ),
  p: (props: any) => (
    <p className="my-4 leading-relaxed text-gray-700" {...props} />
  ),
  pre: (props: any) => {
    const code = props.children?.props?.children || "";
    return <CodeBlockComponent code={code} />;
  },
  code: (props: any) => {
    // Inline code
    if (!props.className) {
      return (
        <code
          className="font-mono bg-gray-100 px-2 py-1 rounded text-sm"
          {...props}
        />
      );
    }
    // Block code (handled by pre)
    return <code className="font-mono" {...props} />;
  },
  ul: (props: any) => (
    <ul className="my-4 list-disc list-inside space-y-2" {...props} />
  ),
  ol: (props: any) => (
    <ol className="my-4 list-decimal list-inside space-y-2" {...props} />
  ),
  li: (props: any) => <li className="text-gray-700" {...props} />,
  blockquote: (props: any) => (
    <blockquote
      className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-600"
      {...props}
    />
  ),
  strong: (props: any) => (
    <strong className="font-bold text-gray-900" {...props} />
  ),
  em: (props: any) => <em className="italic" {...props} />,
};

// Helper function to detect if content is HTML
const isHtmlContent = (content: string): boolean => {
  const trimmed = content.trim();
  return trimmed.startsWith("<") && trimmed.includes(">");
};

export const MarkdownDisplayComponent: FC<MarkdownDisplayProps> = ({
  markdown,
}) => {
  const [mdxSource, setMdxSource] = useState<MDXRemoteSerializeResult | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isHtml, setIsHtml] = useState(false);

  useEffect(() => {
    const processContent = async () => {
      try {
        // Check if content is HTML
        if (isHtmlContent(markdown)) {
          setIsHtml(true);
          setIsLoading(false);
          return;
        }

        // Process as markdown
        const serialized = await serialize(markdown, {
          mdxOptions: {
            rehypePlugins: [
              rehypeRaw,
              [rehypePrettyCode, rehypePrettyCodeOptions],
            ],
          },
        });
        setMdxSource(serialized);
      } catch (error) {
        console.error("Error serializing markdown:", error);
        // Fallback to HTML rendering if markdown processing fails
        setIsHtml(true);
      } finally {
        setIsLoading(false);
      }
    };

    processContent();
  }, [markdown]);

  if (isLoading) {
    return (
      <div className="prose prose-lg max-w-none">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-5/6" />
          <div className="h-4 bg-gray-200 rounded w-4/5" />
        </div>
      </div>
    );
  }

  // Render HTML content directly
  if (isHtml) {
    return (
      <div
        className="prose prose-lg max-w-none
          prose-headings:font-heading prose-headings:text-gray-900
          prose-h1:text-3xl prose-h1:font-bold prose-h1:my-3
          prose-h2:text-2xl prose-h2:font-bold prose-h2:my-3
          prose-h3:text-xl prose-h3:font-semibold prose-h3:my-2
          prose-h4:text-lg prose-h4:font-semibold prose-h4:my-2
          prose-h5:text-base prose-h5:font-semibold prose-h5:my-2
          prose-p:my-2 prose-p:leading-relaxed prose-p:text-gray-700
          prose-ul:my-2 prose-ul:list-disc prose-ul:list-inside prose-ul:space-y-1
          prose-ol:my-2 prose-ol:list-decimal prose-ol:list-inside prose-ol:space-y-1
          prose-li:text-gray-700
          prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:my-3 prose-blockquote:italic prose-blockquote:text-gray-600
          prose-strong:font-bold prose-strong:text-gray-900
          prose-em:italic
          prose-code:font-mono prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm
          prose-pre:my-3 prose-pre:p-4 prose-pre:rounded prose-pre:overflow-x-auto
          [&_code]:bg-[#EADBBB] [&_pre]:bg-[#EADBBB]
          prose-img:rounded-lg prose-img:shadow-md"
        dangerouslySetInnerHTML={{ __html: markdown }}
      />
    );
  }

  // Render markdown content
  if (!mdxSource) {
    return (
      <div className="prose prose-lg max-w-none">
        <p className="text-red-600">Error loading content</p>
      </div>
    );
  }

  return (
    <div className="prose prose-lg max-w-none">
      <MDXRemote {...mdxSource} components={components} />
    </div>
  );
};
